import { type GameCriteria } from './games.types';

/**
 * Serialises an id list the way the backend expects it.
 *
 * `GameCriteriaDto` accepts `1,2,3` or a real array; we always send the
 * comma-separated form, which stays readable in a URL and does not depend on
 * the server's array convention (`ids[]` versus a repeated `ids`).
 *
 * Warning: An empty list means "no filter", not "no genre": without that, a
 * checkbox group with everything unticked would return nothing at all.
 *
 * @param ids Selected ids, possibly empty or absent.
 * @returns The joined list, or `undefined` so the parameter is not sent.
 */
export function idList(ids: readonly number[] | undefined): string | undefined {
  return ids?.length ? ids.join(',') : undefined;
}

/**
 * Translates game criteria into URL parameters.
 *
 * Its own module because three endpoints now accept these criteria — the
 * catalogue, the library and the random pick — and they must not disagree on
 * how a value is encoded. That mirrors `GameCriteriaDto`, which the backend
 * shares for the same reason.
 *
 * @param criteria Filters chosen by the screen.
 * @returns The query parameters, absent values left out.
 */
export function criteriaParams(
  criteria: GameCriteria,
): Record<string, string | number | boolean | undefined> {
  return {
    search: criteria.search,
    playersCount: criteria.playersCount,
    durationMin: criteria.durationMin,
    durationMax: criteria.durationMax,
    complexityMax: criteria.complexityMax,
    difficultyMax: criteria.difficultyMax,
    playerConfig: criteria.playerConfig,
    targetAudience: criteria.targetAudience,
    playEnvironment: criteria.playEnvironment,
    genreIds: idList(criteria.genreIds),
    mechanismIds: idList(criteria.mechanismIds),
    isCustom: criteria.isCustom,
  };
}
